/* eslint-disable react/prop-types */
import React from "react";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSelectedScenarioAsyncAction,
  addScenarioTotalsAsyncAction,
} from "app/scenarios/duck/actions";
import { setTask, removeTask } from "app/common/task/duck/slices";
import TaskProgressSnackbar from "app/common/task/TaskProgressSnackbar";
import _ from "lodash";
import { getPathnameByCycletype } from "lib/utils/cycles";
import { cloneScenario, copyScenario } from "lib/requests/scenarios";
import getStatus, { PollingTimeoutError } from "lib/requests/status";
import mixpanelTrack from "app/common/MixpanelComponent";
import { BsArrowRightShort, BsArrowDownShort } from "react-icons/bs";
import ComparatorLayout from "app/common/ComparatorLayout";
import ConfirmationDialog from "app/common/dialog/ConfirmationDialog";
import SnackbarActionButtons from "app/common/snackbar/SnackbarActionButtons";
import { CopyFromScenarioCard, CopyToScenarioCard } from "./CopyScenarioCard";

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    color: "white",
  },
  scenarioInfoRow: {
    display: "flex",
    alignItems: "center",
    "& :first-child": {
      marginRight: "10px",
    },
  },
  scenarioTitle: {
    marginBottom: "5px",
  },
  dialogRoot: {
    maxWidth: "750px",
  },
  dialogTitle: {
    borderBottom: "1px solid #e2e2e2",
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
  },
  dialogContent: {
    padding: "25px",
  },
  dialogActions: {
    borderTop: "1px solid #e2e2e2",
  },
  sectionTitle: {
    margin: "10px 0px 10px 0px",
  },
  clonedScenarioInfo: {
    border: "1px solid #e2e2e2",
    borderRadius: "3px",
    padding: "10px",
  },
  centerComparatorContainer: {
    display: "flex",
    alignItems: "center",
    margin: "20px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  validTextCycles: {
    color: theme.palette.primary.main,
  },
  validContainerCycles: {
    flex: 1,
    paddingLeft: "24px",
    justifyContent: "start",
  },
}));

const withCopyScenario = (WrappedComponent, scenarioTotal) => {
  return (props) => {
    const theme = useTheme();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(
      false
    );
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const cycleSlice = useSelector((state) => state.cycle);
    const [fromCycle, setFromCycle] = React.useState();
    const [toCycle, setToCycle] = React.useState();
    const [fromScenario, setFromScenario] = React.useState();
    const [toScenarioForm, setToScenarioForm] = React.useState({});
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [arePendingTasks, setArePendingTasks] = React.useState(false);
    const history = useHistory();
    const { url } = useRouteMatch();
    const preventPageUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    React.useEffect(() => {
      if (arePendingTasks)
        window.addEventListener("beforeunload", preventPageUnload);
      else window.removeEventListener("beforeunload", preventPageUnload);
      return () => {
        window.removeEventListener("beforeunload", preventPageUnload);
      };
    }, [arePendingTasks]);

    const handleClickDialogOpen = (e) => {
      e.stopPropagation();
      setDialogOpen(true);
    };

    const handleDialogClose = (e) => {
      e.stopPropagation();
      setDialogOpen(false);
      if (props.onDialogClose) props.onDialogClose(e);
    };

    const enqueueLoadingSnackbar = (action) =>
      enqueueSnackbar(
        `'${fromScenario.name}' (${fromCycle.label}) is being copied 
              over to '${toScenarioForm.scenario.name}' (${toCycle.label})...`,
        {
          persist: true,
          action,
        }
      );

    const enqueueErrorSnackbar = (key) => {
      if (key) closeSnackbar(key);
      enqueueSnackbar(
        `Error when copying '${fromScenario.name}' (${fromCycle.label}) over 
        to '${toScenarioForm.scenario.name}' (${toCycle.label}). Try again shortly`,
        {
          variant: "error",
        }
      );
      setArePendingTasks(false);
    };

    const enqueueSuccessSnackbar = (onAction, snackbarKey) => {
      if (snackbarKey) closeSnackbar(snackbarKey);
      enqueueSnackbar(
        `Scenario '${toScenarioForm.scenario.name}' (${toCycle.label}) successfully created!`,
        {
          variant: "success",
          persist: "true",
          action: (key) => (
            <SnackbarActionButtons
              action="Go to Scenario"
              onAction={onAction}
              key={key}
            />
          ),
        }
      );
    };

    const enqueueTimeoutSnackbar = (onAction, snackbarKey) => {
      if (snackbarKey) closeSnackbar(snackbarKey);
      enqueueSnackbar(
        `Could not update the status of the task. Note that if you dismiss this message, 
                    the task can still be completed, but you will not get notified`,
        {
          variant: "warning",
          persist: "true",
          action: (key) => (
            <SnackbarActionButtons
              action="Reconnect"
              onAction={onAction}
              key={key}
              onDismiss={() => setArePendingTasks(false)}
            />
          ),
        }
      );
    };

    const clone = async () => {
      /* MIXPANEL TRACKS */
      mixpanelTrack(`Clone scenario ${scenarioTotal.scenario.name}`);
      /* END OF MIXPANEL TRACKS */

      const response = await cloneScenario({
        ...toScenarioForm.scenario,
        id: fromScenario.id,
        is_blocked: fromScenario.is_blocked,
      });

      return response.task;
    };

    const copy = async () => {
      const toScenario = { ...toScenarioForm.scenario };
      toScenario.is_blocked = fromScenario.is_blocked;
      /* MIXPANEL TRACKS */
      mixpanelTrack(
        `Copy scenario from ${fromScenario.name} cycle ${fromCycle.label} to cycle ${toCycle.label}`
      );
      /* END OF MIXPANEL TRACKS */

      const response = await copyScenario({
        scenario_from_id: fromScenario.id,
        scenario_to_id: toScenario.id, // Will be undefined if scenario is new (not updating reference)
        cycle_to_id: toCycle.id,
        scenario_user_data: toScenario,
      });

      return response.task;
    };

    const handleCopy = async (event) => {
      handleDialogClose(event);

      let snackbarKey;

      /**
       * If the scenario being updated is not the Reference scenario
       * and it belongs to the same cycle as the 'from' scenario, then
       * we perform a clone operation
       */
      const copyFunction =
        fromCycle.id === toCycle.id &&
        toScenarioForm.scenario?.scenario_type_id !== 1
          ? clone
          : copy;

      try {
        const task = await copyFunction();

        const enqueueTask = async () => {
          setArePendingTasks(true);

          snackbarKey = enqueueLoadingSnackbar(
            <TaskProgressSnackbar
              taskId={task.id}
              size={40}
              className={classes.circularProgress}
            />
          );
          try {
            await getStatus(task.id, (status) => {
              switch (status.task_status) {
                case "FAILURE":
                  enqueueErrorSnackbar(snackbarKey);
                  break;
                case "SUCCESS": {
                  // Success
                  // Update list of scenarios and totals
                  dispatch(
                    addScenarioTotalsAsyncAction({
                      filters: {
                        scenario_type_id: [1, 3],
                      },
                      expand: ["scenario", "scenario.cycle"],
                    })
                  );
                  enqueueSuccessSnackbar(() => {
                    const cycleType = _.find(cycleSlice.type, {
                      id: _.find(cycleSlice.list, {
                        id: status.task_info?.result?.cycle_id,
                      }).cycle_type_id,
                    }).label;
                    const scenarioPath = getPathnameByCycletype(cycleType, url);
                    history.push(scenarioPath);
                    dispatch(
                      changeSelectedScenarioAsyncAction(
                        status.task_info?.result?.scenario_id
                      )
                    );
                  }, snackbarKey);
                  dispatch(removeTask(task.id));
                  setArePendingTasks(false);
                  return;
                }
                case "PENDING":
                  return;
                default:
                  dispatch(
                    setTask({
                      id: task.id,
                      progress:
                        (status.task_info?.step * 100) /
                        status.task_info?.total_steps,
                    })
                  );
              }
            });
          } catch (e) {
            if (e instanceof PollingTimeoutError) {
              enqueueTimeoutSnackbar(enqueueTask, snackbarKey);
            } else throw e;
          }
        };

        await enqueueTask();
      } catch (e) {
        // Log the error somewhere
        enqueueErrorSnackbar(snackbarKey);
      }
    };

    const handleConfirmation = (event, confirmed) => {
      event.stopPropagation();
      setConfirmationDialogOpen(false);

      if (!confirmed) return;

      handleCopy(event);
    };

    const handleCopyClick = async (event) => {
      if (!toScenarioForm.valid) return;

      if (toScenarioForm.scenario?.scenario_type_id === 1) {
        setConfirmationDialogOpen(true);
        return;
      }

      handleCopy(event);
    };

    const handleDialogClick = (e) => {
      /**
       * We need to stop the propagation of the click event when clicking
       * anywhere within the dialog window to prevent side effects
       */
      e.stopPropagation();
      //  setDialogOpen(true);
    };

    return (
      <>
        <WrappedComponent
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          onClick={handleClickDialogOpen}
          onMouseDown={handleClickDialogOpen}
        />
        {!_.isEmpty(scenarioTotal) && (
          <>
            <ConfirmationDialog
              title="Reference prices update"
              content="Reference scenario will also change for your teammates. 
              Additionally, all the comparisons against it will be udpated. 
              Do you want to proceed?"
              open={confirmationDialogOpen}
              onConfirmAction={handleConfirmation}
            />
            <Dialog
              open={dialogOpen}
              onClose={handleDialogClose}
              onClick={handleDialogClick}
              fullWidth
              maxWidth="md"
              disableRestoreFocus
              classes={{
                paperWidthMd: classes.dialogRoot,
              }}
            >
              <DialogTitle className={classes.dialogTitle}>
                Copy scenario prices
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <ComparatorLayout
                  left={
                    <CopyFromScenarioCard
                      onCycleChange={setFromCycle}
                      onSelectedScenario={setFromScenario}
                      defaultScenario={scenarioTotal.scenario}
                    />
                  }
                  center={
                    isSmallScreen ? (
                      <BsArrowDownShort size={26} />
                    ) : (
                      <BsArrowRightShort size={26} />
                    )
                  }
                  centerClassName={classes.centerComparatorContainer}
                  right={
                    fromScenario && (
                      <CopyToScenarioCard
                        onCycleChange={setToCycle}
                        fromScenario={fromScenario}
                        cycles={_.filter(
                          cycleSlice.list,
                          (cycle) =>
                            fromScenario.cycle.delivery_id ===
                              cycle.delivery_id &&
                            cycle.cycle_type_id ===
                              fromScenario.cycle.cycle_type_id
                        )}
                        onFormChange={setToScenarioForm}
                      />
                    )
                  }
                />
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                {(fromCycle?.cycle_type_id === 3 &&
                  toCycle?.cycle_type_id !== 3) ||
                (fromCycle?.cycle_type_id !== 3 &&
                  toCycle?.cycle_type_id === 3) ? (
                  <di className={classes.validContainerCycles}>
                    <Typography
                      variant="body2"
                      className={classes.validTextCycles}
                    >
                      Invalid from-to cycles combination. Try another one
                    </Typography>
                  </di>
                ) : null}
                <Button autoFocus onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    !toScenarioForm.valid ||
                    (fromCycle?.cycle_type_id === 3 &&
                      toCycle?.cycle_type_id !== 3) ||
                    (fromCycle?.cycle_type_id !== 3 &&
                      toCycle?.cycle_type_id === 3)
                  }
                  variant="contained"
                  onClick={handleCopyClick}
                  color="primary"
                >
                  Copy
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </>
    );
  };
};

export default withCopyScenario;
